import { CheckCircle, Lock, Circle, Calendar } from 'lucide-react';

interface Client {
  id: string;
  name: string;
  email: string;
  intakeCompleted: boolean;
  intakeCompletedDate?: string;
}

interface EligibilityStatus {
  appointmentTypeId: string;
  isEligible: boolean;
  reason?: string;
  nextAvailableDate?: string;
  hasCompleted?: boolean;
}

interface AppointmentFlowProps {
  client: Client;
  eligibility: EligibilityStatus[];
}

export function AppointmentFlow({ client, eligibility }: AppointmentFlowProps) {
  const initialEligibility = eligibility.find(e => e.appointmentTypeId === 'initial-consultation');
  const followUpEligibility = eligibility.filter(e => 
    ['follow-up-60', 'follow-up-30'].includes(e.appointmentTypeId)
  );
  
  const steps = [
    {
      id: 'intake',
      name: 'Intake Form',
      description: 'Complete health assessment',
      completed: client.intakeCompleted,
      date: client.intakeCompletedDate,
      icon: CheckCircle,
      eligible: true
    },
    {
      id: 'initial',
      name: 'Initial Consultation',
      description: 'Book your first appointment',
      completed: initialEligibility?.hasCompleted || false,
      eligible: initialEligibility?.isEligible || false,
      icon: initialEligibility?.hasCompleted ? CheckCircle : 
            initialEligibility?.isEligible ? Circle : Lock,
      reason: initialEligibility?.reason
    },
    {
      id: 'followup',
      name: 'Follow-up Sessions',
      description: 'Continue your wellness journey',
      eligible: followUpEligibility.some(e => e.isEligible),
      completed: followUpEligibility.some(e => e.hasCompleted),
      icon: followUpEligibility.some(e => e.hasCompleted) ? CheckCircle :
            followUpEligibility.some(e => e.isEligible) ? Circle : Lock,
      reason: followUpEligibility.find(e => !e.isEligible)?.reason
    }
  ];

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Your Wellness Journey</h3>
        <div className="text-sm text-gray-500">
          Welcome back, {client.name.split(' ')[0]}! 🌿
        </div>
      </div>
      
      <div className="relative">
        {/* Progress Line */}
        <div className="absolute top-4 left-4 right-4 h-0.5 bg-gray-200 z-0" />
        <div 
          className="absolute top-4 left-4 h-0.5 bg-green-500 z-0 transition-all duration-500"
          style={{ 
            width: `${Math.min(
              ((steps.filter(s => s.completed).length) / (steps.length - 1)) * 100, 
              100
            )}%` 
          }}
        />
        
        {/* Steps */}
        <div className="flex items-start justify-between relative z-10">
          {steps.map((step) => {
            const IconComponent = step.icon;
            
            return (
              <div key={step.id} className="flex flex-col items-center max-w-xs">
                {/* Icon */}
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center mb-2 transition-colors
                  ${step.completed 
                    ? 'bg-green-500 text-white' 
                    : step.eligible 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-300 text-gray-500'
                  }
                `}>
                  <IconComponent className="w-4 h-4" />
                </div>
                
                {/* Step Info */}
                <div className="text-center">
                  <h4 className={`
                    text-sm font-medium mb-1
                    ${step.completed 
                      ? 'text-green-700' 
                      : step.eligible 
                        ? 'text-blue-700' 
                        : 'text-gray-500'
                    }
                  `}>
                    {step.name}
                  </h4>
                  <p className="text-xs text-gray-600 mb-2">
                    {step.description}
                  </p>
                  
                  {/* Status */}
                  {step.completed ? (
                    <div className="flex items-center justify-center text-xs text-green-600">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Completed
                    </div>
                  ) : step.eligible ? (
                    <div className="text-xs text-blue-600 font-medium">
                      Ready to book
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500">
                      {step.reason || 'Not available yet'}
                    </div>
                  )}
                  
                  {/* Date info */}
                  {step.date && (
                    <div className="text-xs text-gray-400 mt-1">
                      {new Date(step.date).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Next Action CTA */}
      <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
        {!client.intakeCompleted ? (
          <div className="text-center">
            <p className="text-green-800 font-medium mb-2">
              Complete your intake form to get started
            </p>
            <a 
              href="/intake" 
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              Complete Intake Form
            </a>
          </div>
        ) : !initialEligibility?.hasCompleted && initialEligibility?.isEligible ? (
          <div className="text-center">
            <p className="text-green-800 font-medium mb-2">
              You're ready to book your initial consultation!
            </p>
            <button 
              onClick={() => {
                const appointmentSection = document.getElementById('appointment-types');
                appointmentSection?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Book Initial Consultation
            </button>
          </div>
        ) : followUpEligibility.some(e => e.isEligible) ? (
          <div className="text-center">
            <p className="text-green-800 font-medium mb-2">
              Continue your wellness journey with a follow-up session
            </p>
            <button 
              onClick={() => {
                const appointmentSection = document.getElementById('appointment-types');
                appointmentSection?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Book Follow-up Session
            </button>
          </div>
        ) : (
          <div className="text-center">
            <p className="text-green-800 font-medium">
              Great progress on your wellness journey! 🌟
            </p>
            <p className="text-green-700 text-sm mt-1">
              Check back soon for your next available appointment
            </p>
          </div>
        )}
      </div>
    </div>
  );
}